import React, { useContext, useEffect, useState } from 'react';
import ABResultHeader from './ABResultHeader';
import ResultAnalytics from './ResultAnalytics';
import ResultInfo from './ResultInfo';
import { computeABTestResult } from '../../services/ABServices';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { ABContext } from '../../contexts/ABContext';
import EmptyState from 'Components/DesignSystem/EmptyState/EmptyState';
import NoDataIllustration from 'Components/DesignSystem/EmptyState/NoDataIllustration';
import PageSkeleton from 'Components/DesignSystem/PageSkeleton/PageSkeleton';

/**
 * The results page shell (`okf/design-system/e2e.md` § 156).
 *
 * `computeABTestResult` on mount, `toast.info` on a `done:false` or a missing testId —
 * all unchanged. What moved is the two non-content states: `TableSkeletonElement` (a
 * table skeleton on a page whose top half is not a table) becomes `PageSkeleton`, and
 * `NoDataComponent`'s bare "No Data Found" becomes an `EmptyState` that says which test
 * could not be loaded and offers the way back.
 */

type TABResultPageProps = {
    testId?: string;
}

const ABResultPage = ({ testId }: TABResultPageProps) => {
    const [loading, setLoading] = useState<boolean>(false)

    const abContext = useContext(ABContext);
    const { testData, setTestData } = abContext

    const getTestResult = async () => {
        try {
            if (testId) {
                setLoading(true)
                const res = await computeABTestResult(testId as string)
                if (res?.done) {
                    setTestData(res?.data)
                }
                else toast.info(res?.message)
            }
            else toast.info("test id not found")
        } catch (error) {
            setLoading(false)
        }
        finally { setLoading(false) }
    }

    useEffect(() => {
        getTestResult()
    }, [])

    if (loading) {
        /* `detail`: title+actions over stacked rows — the shape this page actually has
           (header, then two cards, then the charts). `list` would draw a table skeleton
           where the traffic-allocation card goes. */
        return <PageSkeleton preset="detail" data-testid="ab-result-loading" />;
    }

    /*
      `[undefined, null].includes(testData)` is preserved verbatim. It is a roundabout
      nullish check, but `testData` is `any` from the context and narrowing it to
      `== null` would change behaviour for a value like `0` or `''` that the API could
      in principle return.
    */
    if ([undefined, null].includes(testData)) {
        return (
            <EmptyState
                illustration={<NoDataIllustration />}
                title="No results for this test"
                description="The test has no computed results yet, or it could not be loaded."
                size="md"
                data-testid="ab-result-empty"
            />
        );
    }

    return (
        <>
            <ABResultHeader testName={testData?.name ?? ''} testId={testId as string} />
            <ResultInfo />
            <ResultAnalytics testId={testId as string} />
        </>
    );
}

export default ABResultPage;